import { useState, useRef, useEffect } from 'react';
import type { AppLockInstance } from '@ascend/applock';
import styles from './AppLock.module.css';

export function LockMenu({ lock }: { lock: AppLockInstance }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'menu' | 'change' | 'remove'>('menu');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [message, setMessage] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setMode('menu');
        setMessage('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleChangePassword = async () => {
    if (!currentPw || !newPw || newPw.length < 6) {
      setMessage('Enter current and new password (min 6 chars).');
      return;
    }
    try {
      await lock.changePassword(currentPw, newPw);
      setMessage('Password changed.');
      setCurrentPw('');
      setNewPw('');
      setTimeout(() => { setOpen(false); setMode('menu'); setMessage(''); }, 1000);
    } catch {
      setMessage('Current password incorrect.');
    }
  };

  const handleRemovePassword = async () => {
    if (!currentPw) return;
    try {
      await lock.removePassword(currentPw);
      setMessage('Password removed. Data decrypted.');
      setTimeout(() => { setOpen(false); setMode('menu'); setMessage(''); }, 1000);
    } catch {
      setMessage('Password incorrect.');
    }
  };

  return (
    <div ref={ref} className={styles.menuContainer}>
      <button
        onClick={() => { setOpen(!open); setMode('menu'); setMessage(''); }}
        title="App lock settings"
        aria-label="App lock settings"
        className={styles.trigger}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      </button>

      {open && (
        <div className={styles.dropdown}>
          {mode === 'menu' && (
            <>
              <button
                onClick={() => lock.lock('manual')}
                className={styles.menuItem}
              >
                Lock Now
              </button>
              <button
                onClick={() => { setMode('change'); setMessage(''); }}
                className={styles.menuItem}
              >
                Change Password
              </button>
              <button
                onClick={() => { setMode('remove'); setMessage(''); }}
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
              >
                Remove Password
              </button>
            </>
          )}

          {mode === 'change' && (
            <div className={styles.menuForm}>
              <input
                type="password"
                placeholder="Current password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className={styles.menuInput}
              />
              <input
                type="password"
                placeholder="New password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className={styles.menuInput}
              />
              <button
                type="button"
                onClick={handleChangePassword}
                className={styles.actionButton}
              >
                Change
              </button>
              <button
                type="button"
                onClick={() => { setMode('menu'); setMessage(''); }}
                className={styles.ghostButton}
              >
                Back
              </button>
              {message && <div className={styles.message}>{message}</div>}
            </div>
          )}

          {mode === 'remove' && (
            <div className={styles.menuForm}>
              <p className={styles.hint}>This will decrypt all stored data and remove the password.</p>
              <input
                type="password"
                placeholder="Current password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className={styles.menuInput}
              />
              <button
                type="button"
                onClick={handleRemovePassword}
                className={styles.dangerButton}
              >
                Remove
              </button>
              <button
                type="button"
                onClick={() => { setMode('menu'); setMessage(''); setCurrentPw(''); }}
                className={styles.ghostButton}
              >
                Cancel
              </button>
              {message && <div className={styles.message}>{message}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
